import { createContext, useCallback, useEffect, useRef, useState } from 'react'

import BROWSERS from '../enums/browsers.js'
import USER_AGENT_KEYWORDS_MAPPING from '../enums/userAgentKeywords.js'
import { getBrowserRegexMatches } from '../utils/proctoring.js'

const ProctoringContext = createContext()
const AV_PLAYBACK_ID = 'av-playback'
const SCREEN_PLAYBACK_ID = 'screen-playback'
const AV_PLAYBACK_CANVAS_ID = 'av-playback-canvas'
const SCREEN_PLAYBACK_CANVAS_ID = 'screen-playback-canvas'
const PROCTORING_WRAPPER_ID = 'proctoring-videos-wrapper'

const ProctoringProvider = ({ children }) => {
    const [isVideoCaptureAvailable, setVideoCaptureAvailable] = useState(false)
    const [isAudioCaptureAvailable, setAudioCaptureAvailable] = useState(false)
    const [isScreenCaptureAvailable, setScreenCaptureAvailable] =
        useState(false)
    const avStreamRef = useRef(null)
    const audioStreamRef = useRef(null)
    const videoStreamRef = useRef(null)
    const screenStreamRef = useRef(null)
    const _onCutRef = useRef()
    const _onCopyRef = useRef()
    const _onPasteRef = useRef()
    const _onDragStartRef = useRef()
    const _onDropRef = useRef()
    const _onWindowResizeRef = useRef()
    const _onWindowFocusRef = useRef()
    const _onWindowBlurRef = useRef()
    const _onVisibilityChangeRef = useRef()
    const isCutEnabled = useRef(true)
    const isCopyEnabled = useRef(true)
    const isPasteEnabled = useRef(true)
    const isDragStartEnabled = useRef(true)
    const isDropEnabled = useRef(true)
    const isEntireScreenShareEnabled = useRef(false)
    const [avImageCaptureInterval, setAvImageCaptureInterval] = useState(10000)
    const [screenImageCaptureInterval, setScreenImageCaptureInterval] =
        useState(10000)
    const _onAvImageRef = useRef()
    const _onScreenImageRef = useRef()
    const _avImageIntervalRef = useRef()
    const _screenImageIntervalRef = useRef()
    const _onAvErrorRef = useRef()
    const _onAudioErrorRef = useRef()
    const _onVideoErrorRef = useRef()
    const _onScreenErrorRef = useRef()

    const startVideoCapture = useCallback(async () => {
        try {
            // Get video stream only with specified constraints
            const videoStream = await navigator.mediaDevices.getUserMedia({
                video: { width: 640 },
                audio: false,
            })

            // Create and configure video element
            const videoElement = document.createElement('video')
            videoElement.srcObject = videoStream
            videoElement.autoplay = true
            videoElement.muted = true
            videoElement.id = AV_PLAYBACK_ID

            // Append video element to the wrapper
            document
                .getElementById(PROCTORING_WRAPPER_ID)
                .appendChild(videoElement)

            // Once metadata is loaded, create and append a canvas element
            videoElement.onloadedmetadata = () => {
                const canvasElement = document.createElement('canvas')
                canvasElement.width = videoElement.videoWidth
                canvasElement.height = videoElement.videoHeight
                canvasElement.id = AV_PLAYBACK_CANVAS_ID
                document
                    .getElementById(PROCTORING_WRAPPER_ID)
                    .appendChild(canvasElement)
            }

            // Update state and ref
            setVideoCaptureAvailable(true)
            videoStreamRef.current = videoStream

            // Attach event to handle track ending
            for (const track of videoStream.getTracks()) {
                track.onended = () => {
                    setVideoCaptureAvailable(false)
                    videoStreamRef.current = null
                    document.getElementById(AV_PLAYBACK_ID)?.remove()
                    document.getElementById(AV_PLAYBACK_CANVAS_ID)?.remove()
                }
            }
        } catch (e) {
            // Cleanup on error
            videoStreamRef.current = null
            document.getElementById(AV_PLAYBACK_ID)?.remove()
            document.getElementById(AV_PLAYBACK_CANVAS_ID)?.remove()
            _onAvErrorRef.current?.(e)
            _onVideoErrorRef.current?.(e)
        }
    }, [])

    const stopVideoCapture = () => {
        if (videoStreamRef.current) {
            for (const track of videoStreamRef.current.getTracks()) {
                track.stop()
            }
            videoStreamRef.current = null
            setVideoCaptureAvailable(false)

        }
        document.getElementById(AV_PLAYBACK_ID)?.remove()
        document.getElementById(AV_PLAYBACK_CANVAS_ID)?.remove()
    }

    // Audio capturing function
    const startAudioCapture = useCallback(async () => {
        try {
            // Get audio stream only
            const audioStream = await navigator.mediaDevices.getUserMedia({
                audio: true,
                video: false,
            })

            // Update state and ref
            setAudioCaptureAvailable(true)
            audioStreamRef.current = audioStream

            // Handle track end events for audio
            for (const track of audioStream.getTracks()) {
                track.onended = () => {
                    setAudioCaptureAvailable(false)
                    audioStreamRef.current = null
                }

            }
        } catch (e) {
            // Cleanup on error
            audioStreamRef.current = null
            _onAvErrorRef.current?.(e)
            _onAudioErrorRef.current?.(e)
        }
    }, [])

    const stopAudioCapture = () => {
        if (audioStreamRef.current) {
            for (const track of audioStreamRef.current.getTracks()) {
                track.stop()
            }
            audioStreamRef.current = null
            setAudioCaptureAvailable(false)
        }
    }

    const startAVCapture = useCallback(async () => {
        try {
            const videoElement = document.createElement('video')
            const avStream = await navigator.mediaDevices.getUserMedia({
                audio: true,
                video: {
                    width: 640,
                },
            })
            videoElement.srcObject = avStream
            videoElement.autoplay = true
            videoElement.muted = true
            videoElement.id = AV_PLAYBACK_ID
            const wrapper = document.getElementById(PROCTORING_WRAPPER_ID)
            wrapper.appendChild(videoElement)

            videoElement.onloadedmetadata = () => {
                const canvasElement = document.createElement('canvas')
                canvasElement.width = videoElement.videoWidth
                canvasElement.height = videoElement.videoHeight
                canvasElement.id = AV_PLAYBACK_CANVAS_ID
                wrapper.appendChild(canvasElement)
            }

            setVideoCaptureAvailable(true)
            setAudioCaptureAvailable(true)

            avStreamRef.current = avStream

            avStream.getTracks().forEach(x => {
                x.onended = () => {
                    if (x.kind === 'video') {
                        setVideoCaptureAvailable(false)
                    } else if (x.kind === 'audio') {
                        setAudioCaptureAvailable(false)
                    }
                    avStreamRef.current = null
                    document.getElementById(AV_PLAYBACK_ID)?.remove()
                    document.getElementById(AV_PLAYBACK_CANVAS_ID)?.remove()
                }
                return x
            })
        } catch (e) {
            console.error('startAVCapture: Error occurred', e)
            avStreamRef.current = null
            document.getElementById(AV_PLAYBACK_ID)?.remove()
            document.getElementById(AV_PLAYBACK_CANVAS_ID)?.remove()
            _onAvErrorRef.current && _onAvErrorRef.current(e)
        }
    }, [])

    const startScreenCapture = useCallback(async () => {
        try {
            const videoElement = document.createElement('video')
            const screenStream = await navigator.mediaDevices.getDisplayMedia({
                video: { mediaSource: 'screen', displaySurface: 'monitor' },
                selfBrowserSurface: 'include',
            })
            videoElement.srcObject = screenStream
            videoElement.autoplay = true
            videoElement.muted = true
            videoElement.id = SCREEN_PLAYBACK_ID
            const wrapper = document.getElementById(PROCTORING_WRAPPER_ID)
            wrapper.appendChild(videoElement)

            videoElement.onloadedmetadata = () => {
                const canvasElement = document.createElement('canvas')
                canvasElement.width = videoElement.videoWidth
                canvasElement.height = videoElement.videoHeight
                canvasElement.id = SCREEN_PLAYBACK_CANVAS_ID
                wrapper.appendChild(canvasElement)
            }

            setScreenCaptureAvailable(true)

            screenStreamRef.current = screenStream

            let screenType
            // make default as `monitor` if the browser is not in the supported list
            switch (getBrowserName()) {
                case BROWSERS.CHROME:
                case BROWSERS.EDGE:
                case BROWSERS.SAFARI:
                case BROWSERS.BRAVE:
                case BROWSERS.ARC:
                case BROWSERS.OPERA:
                    screenType = screenStream
                        ?.getVideoTracks()[0]
                        .getCapabilities().displaySurface
                    break
                default:
                    screenType = 'monitor'
            }

            if (
                screenType !== 'monitor' &&
                isEntireScreenShareEnabled.current
            ) {
                const screenShareError = new Error(
                    'Please share your entire screen to start the test.'
                )
                _onScreenErrorRef.current &&
                    _onScreenErrorRef.current(screenShareError)
                stopScreenCapture()
            }

            screenStream.getVideoTracks()[0].onended = () => {
                setScreenCaptureAvailable(false)
                screenStreamRef.current = null
                document.getElementById(SCREEN_PLAYBACK_ID)?.remove()
                document.getElementById(SCREEN_PLAYBACK_CANVAS_ID)?.remove()
            }
        } catch (e) {
            screenStreamRef.current = null
            document.getElementById(SCREEN_PLAYBACK_ID)?.remove()
            document.getElementById(SCREEN_PLAYBACK_CANVAS_ID)?.remove()
            _onScreenErrorRef.current && _onScreenErrorRef.current(e)
        }
    }, [])

    const stopAVCapture = () => {
        if (avStreamRef.current) {
            avStreamRef.current.getTracks().forEach(track => {
                track.stop()
                if (track.kind === 'video') {
                    setVideoCaptureAvailable(false)
                } else if (track.kind === 'audio') {
                    setAudioCaptureAvailable(false)
                }
            })
            avStreamRef.current = null
        }
        document.getElementById(AV_PLAYBACK_ID)?.remove()
        document.getElementById(AV_PLAYBACK_CANVAS_ID)?.remove()
    }

    const stopScreenCapture = () => {
        if (screenStreamRef.current) {
            screenStreamRef.current.getTracks().forEach(track => {
                track.stop()
                setScreenCaptureAvailable(false)
            })
            screenStreamRef.current = null
        }
        document.getElementById(SCREEN_PLAYBACK_ID)?.remove()
        document.getElementById(SCREEN_PLAYBACK_CANVAS_ID)?.remove()
    }

    const setCutEnabled = val => {
        isCutEnabled.current = val
    }

    const setCopyEnabled = val => {
        isCopyEnabled.current = val
    }

    const setPasteEnabled = val => {
        isPasteEnabled.current = val
    }

    const setDragStartEnabled = val => {
        isDragStartEnabled.current = val
    }

    const setDropEnabled = val => {
        isDropEnabled.current = val
    }

    const setEntireScreenShareEnabled = val => {
        isEntireScreenShareEnabled.current = val
    }

    const _clearClipboard = e => {
        e.clipboardData.setData('text/plain', '')
    }

    const _onCut = e => {
        // Get cut data
        const cutData = window.getSelection().toString()
        _onCutRef?.current && _onCutRef.current(cutData, e)

        if (!isCutEnabled.current) {
            e.preventDefault()
            _clearClipboard(e)
        }
    }

    const _onCopy = e => {
        // Get copied data
        const copiedData = window.getSelection().toString()
        _onCopyRef?.current && _onCopyRef.current(copiedData, e)

        if (!isCopyEnabled.current) {
            e.preventDefault()
            _clearClipboard(e)
        }
    }

    const _onPaste = e => {
        // Get pasted data via clipboard API
        let clipboardData = e.clipboardData || window?.clipboardData
        const pastedData = clipboardData?.getData('Text')
        _onPasteRef?.current && _onPasteRef.current(pastedData, e)

        if (!isPasteEnabled.current) {
            e.preventDefault()
            _clearClipboard(e)
            return false
        }
    }

    const _onDragStart = e => {
        const dragText = e.dataTransfer.getData('Text')
        _onDragStartRef?.current && _onDragStartRef?.current(dragText, e)

        if (!isDragStartEnabled.current) {
            e.preventDefault()
            return false
        }
    }

    const _onDrop = e => {
        const dragText = e.dataTransfer.getData('Text')
        _onDropRef?.current && _onDropRef?.current(dragText, e)

        if (!isDropEnabled.current) {
            e.preventDefault()
            return false
        }
    }

    const _onWindowResize = e => {
        _onWindowResizeRef?.current &&
            _onWindowResizeRef?.current(
                { width: window.innerWidth, height: window.innerHeight },
                e
            )
    }

    const _onWindowFocus = e => {
        _onWindowFocusRef?.current && _onWindowFocusRef?.current(e)
    }

    const _onWindowBlur = e => {
        _onWindowBlurRef?.current && _onWindowBlurRef?.current(e)
    }

    const _onVisibilityChange = e => {
        _onVisibilityChangeRef?.current && _onVisibilityChangeRef?.current(e)
    }

    const attachOnCut = callback => {
        _onCutRef.current = callback
    }
    const attachOnCopy = callback => {
        _onCopyRef.current = callback
    }
    const attachOnPaste = callback => {
        _onPasteRef.current = callback
    }
    const attachOnDragStart = callback => {
        _onDragStartRef.current = callback
    }
    const attachOnDrop = callback => {
        _onDropRef.current = callback
    }
    const attachOnWindowResize = callback => {
        _onWindowResizeRef.current = callback
    }
    const attachOnWindowFocus = callback => {
        _onWindowFocusRef.current = callback
    }
    const attachOnWindowBlur = callback => {
        _onWindowBlurRef.current = callback
    }
    const attachOnVisibilityChange = callback => {
        _onVisibilityChangeRef.current = callback
    }
    const attachOnAvImage = callback => {
        _onAvImageRef.current = callback
    }
    const attachOnScreenImage = callback => {
        _onScreenImageRef.current = callback
    }
    const attachOnAvError = callback => {
        _onAvErrorRef.current = callback
    }
    const attachOnAudioError = callback => {
        _onAudioErrorRef.current = callback
    }
    const attachOnVideoError = callback => {
        _onVideoErrorRef.current = callback
    }
    const attachOnScreenError = callback => {
        _onScreenErrorRef.current = callback
    }

    // Listen to dom events on window document
    useEffect(() => {
        document.addEventListener('cut', _onCut)
        document.addEventListener('copy', _onCopy)
        document.addEventListener('paste', _onPaste)
        document.addEventListener('dragstart', _onDragStart)
        document.addEventListener('drop', _onDrop)
        window.addEventListener('resize', _onWindowResize)
        window.addEventListener('focus', _onWindowFocus)
        window.addEventListener('blur', _onWindowBlur)
        document.addEventListener('visibilitychange', _onVisibilityChange)

        // Unsubscribe event listeners and clear intervals
        return () => {
            document.removeEventListener('cut', _onCut)
            document.removeEventListener('copy', _onCopy)
            document.removeEventListener('paste', _onPaste)
            document.removeEventListener('dragstart', _onDragStart)
            document.removeEventListener('drop', _onDrop)
            window.removeEventListener('resize', _onWindowResize)
            window.removeEventListener('focus', _onWindowFocus)
            window.removeEventListener('blur', _onWindowBlur)
            document.removeEventListener(
                'visibilitychange',
                _onVisibilityChange
            )
            clearInterval(_avImageIntervalRef.current)
            clearInterval(_screenImageIntervalRef.current)
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const startAvCaptureTimer = (interval = avImageCaptureInterval) => {
        // Clear any prior interval; otherwise the old one keeps firing
        // with no handle to stop it.
        if (_avImageIntervalRef.current) {
            clearInterval(_avImageIntervalRef.current)
        }
        _avImageIntervalRef.current = setInterval(async () => {
            // Generate image from stream
            const imgData = await getAvImageData()
            // Call user defined callback if any
            _onAvImageRef?.current && _onAvImageRef?.current(imgData)
        }, interval)
    }

    const stopAvCaptureTimer = () => {
        _avImageIntervalRef?.current &&
            clearInterval(_avImageIntervalRef.current)
    }

    const startScreenCaptureTimer = (interval = screenImageCaptureInterval) => {
        // Same guard as startAvCaptureTimer.
        if (_screenImageIntervalRef.current) {
            clearInterval(_screenImageIntervalRef.current)
        }
        _screenImageIntervalRef.current = setInterval(async () => {
            // Generate image from stream
            // screenStreamRef.current
            const imgData = await getScreenImageData()
            // Call user defined callback if any
            _onScreenImageRef?.current && _onScreenImageRef?.current(imgData)
        }, interval)
    }

    const stopScreenCaptureTimer = () => {
        _screenImageIntervalRef?.current &&
            clearInterval(_screenImageIntervalRef.current)
    }

    // Pull a fresh frame from a live MediaStreamTrack.
    //
    // Why this is rewritten: the previous implementation used
    // `videoElement.currentTime += 0.0001` + a 500ms setTimeout to "force
    // a frame refresh" before `drawImage`. That trick is a no-op for live
    // MediaStream sources — `currentTime` only advances meaningfully for
    // recorded media. When the hidden `<video>` element got throttled
    // (background tab, macOS memory pressure, off-screen heuristics),
    // `drawImage` kept drawing the last decoded frame and every uploaded
    // proctoring snapshot was identical.
    //
    // New strategy:
    //  1. Preferred: `ImageCapture.grabFrame()` directly on the track.
    //     Skips the `<video>` element entirely, so browser throttling on
    //     hidden videos cannot freeze the frame.
    //  2. Fallback: existing `<video>` element + `requestVideoFrameCallback`
    //     to wait for an actual decoded frame before drawing.
    //  3. Last resort (old browsers without rVFC): draw immediately after
    //     `play()` resolves.
    const _drawTrackToCanvas = async (canvas, bitmapOrVideo, w, h) => {
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        if (!ctx) throw new Error('Canvas context error')
        ctx.drawImage(bitmapOrVideo, 0, 0)
        return canvas.toDataURL('image/jpeg', 0.8)
    }

    const _grabViaImageCapture = async (videoTrack, canvasEl) => {
        const ic = new window.ImageCapture(videoTrack)
        const bitmap = await ic.grabFrame()
        try {
            const canvas = canvasEl || document.createElement('canvas')
            return await _drawTrackToCanvas(
                canvas,
                bitmap,
                bitmap.width,
                bitmap.height
            )
        } finally {
            bitmap.close?.()
        }
    }

    const _grabViaVideoElement = (videoTrack, videoElement, canvasEl, type, retries) =>
        new Promise((resolve, reject) => {
            // If the caller passed us an existing <video>, reuse it (its
            // metadata is already loaded). Otherwise create a throwaway
            // one here — we'll wait for `loadedmetadata` before reading
            // its dimensions.
            const isThrowaway = !videoElement
            const ve =
                videoElement ||
                Object.assign(document.createElement('video'), {
                    srcObject: new MediaStream([videoTrack]),
                    autoplay: true,
                    muted: true,
                    playsInline: true,
                })

            const cleanup = () => {
                if (!isThrowaway) return
                ve.pause?.()
                ve.onloadedmetadata = null
                ve.srcObject = null
            }
            const resolveWithCleanup = value => {
                cleanup()
                resolve(value)
            }
            const rejectWithCleanup = error => {
                cleanup()
                reject(error)
            }

            const onReady = () => {
                if (ve.videoWidth === 0 || ve.videoHeight === 0) {
                    if (retries <= 0) {
                        console.error(
                            `Video dimensions still 0x0 after retries for ${type}`
                        )
                        return rejectWithCleanup(
                            new Error('Video dimensions are not updating')
                        )
                    }
                    setTimeout(() => {
                        _grabViaVideoElement(
                            videoTrack,
                            ve,
                            canvasEl,
                            type,
                            retries - 1
                        )
                            .then(resolveWithCleanup)
                            .catch(rejectWithCleanup)
                    }, 500)
                    return
                }

                // rVFC never fires in a throttled/background tab, which would
                // hang this promise (leaking one per capture tick). The 1s
                // fallback draws anyway; `called` keeps it to a single draw.
                let called = false
                let fallbackTimer = null
                const draw = () => {
                    if (called) return
                    called = true
                    if (fallbackTimer) clearTimeout(fallbackTimer)
                    const canvas = canvasEl || document.createElement('canvas')
                    _drawTrackToCanvas(canvas, ve, ve.videoWidth, ve.videoHeight)
                        .then(resolveWithCleanup)
                        .catch(rejectWithCleanup)
                }

                if (typeof ve.requestVideoFrameCallback === 'function') {
                    fallbackTimer = setTimeout(draw, 1000)
                    ve.requestVideoFrameCallback(draw)
                } else {
                    // Browsers without rVFC — best-effort: ensure play()
                    // has resolved (a decoded frame is present) then draw.
                    Promise.resolve(ve.play?.()).then(draw, draw)
                }
            }

            if (!videoElement) {
                ve.onloadedmetadata = onReady
            } else {
                onReady()
            }
        })

    const _getImageDataFromTrack = async (
        videoTrack,
        videoElement = null,
        canvasEl = null,
        type,
        retries = 5
    ) => {
        if (!videoTrack) throw new Error(`No track for ${type}`)

        if (typeof window.ImageCapture === 'function') {
            try {
                return await _grabViaImageCapture(videoTrack, canvasEl)
            } catch (e) {
                console.warn(
                    `[ImageCapture] grabFrame failed for ${type}, falling back: ${e.message}`
                )
            }
        }

        return _grabViaVideoElement(
            videoTrack,
            videoElement,
            canvasEl,
            type,
            retries
        )
    }

    const getAvImageData = async () => {
        let imgData = null
        try {
            imgData = await _getImageDataFromTrack(
                avStreamRef.current.getVideoTracks()[0],
                document.getElementById(AV_PLAYBACK_ID),
                document.getElementById(AV_PLAYBACK_CANVAS_ID),
                'webcam'
            )
        } catch (e) {
            console.error('Error in webcam image-data generation', e)
        }
        return imgData
    }

    const getScreenImageData = async () => {
        let imgData = null
        try {
            imgData = await _getImageDataFromTrack(
                screenStreamRef.current.getVideoTracks()[0],
                document.getElementById(SCREEN_PLAYBACK_ID),
                document.getElementById(SCREEN_PLAYBACK_CANVAS_ID),
                'screen'
            )
        } catch (e) {
            console.error('Error in screen image-data generation', e)
        }
        return imgData
    }

    const getUserAgentData = () => {
        return navigator?.userAgentData?.brands || []
    }

    const getBrowserName = () => {
        const userAgentString = navigator.userAgent || ''

        // Convert to lower case and treat tabs and multiple spaces as single space
        const sanitizedUserAgent = userAgentString
            .toLowerCase()
            .replace(/\s\s+/g, ' ')

        const browserRegexMatches = getBrowserRegexMatches(sanitizedUserAgent)

        let browserName = 'unknown'

        // detect the browser name using grouping in regex
        // please refer `https://github.com/sibiraj-s/browser-dtector/blob/master/lib/browser-dtector.ts`
        const detectedName =
            browserRegexMatches[5] ||
            browserRegexMatches[3] ||
            browserRegexMatches[1] ||
            null

        if (USER_AGENT_KEYWORDS_MAPPING[detectedName.toUpperCase()]) {
            browserName =
                USER_AGENT_KEYWORDS_MAPPING[detectedName.toUpperCase()]
        }

        // Handle special cases where browser name needs to be adjusted
        if (detectedName === 'chrome') {
            // Brave exposes a `navigator.brave` object
            if (typeof navigator?.brave?.isBrave === 'function') {
                browserName = BROWSERS.BRAVE
            }

            // Arc browser have a property called `--arc-palette-title` in the root element
            if (
                getComputedStyle(document.documentElement).getPropertyValue(
                    '--arc-palette-title'
                )
            ) {
                browserName = BROWSERS.ARC
            }
        }

        return browserName
    }

    const getBrowserVersion = () => {
        var browser = navigator.userAgent.toLowerCase()
        let version = 'Unknown'

        if (browser.indexOf('msie') != -1) {
            // Internet Explorer
            version = parseInt(browser.split('msie')[1])
        } else if (browser.indexOf('firefox') != -1) {
            // Firefox
            version = parseInt(browser.split('firefox/')[1])
        } else if (browser.indexOf('chrome') != -1) {
            // Chrome
            version = parseInt(browser.split('chrome/')[1])
        } else if (browser.indexOf('safari') != -1) {
            // Safari
            version = parseInt(browser.split('version/')[1])
        } else if (browser.indexOf('opera') != -1) {
            // Opera
            version = parseInt(browser.split('opr/')[1])
        } else {
            // Other browser
            version = 'Unknown'
        }
        return version
    }

    const getOS = () => {
        let OSName = 'Unknown'
        if (navigator.userAgent.indexOf('Win') != -1) {
            OSName = 'Windows'
        } else if (navigator.userAgent.indexOf('Mac') != -1) {
            OSName = 'Macintosh'
        } else if (navigator.userAgent.indexOf('Linux') != -1) {
            OSName = 'Linux'
        } else if (navigator.userAgent.indexOf('Android') != -1) {
            OSName = 'Android'
        } else if (navigator.userAgent.indexOf('iOS') != -1) {
            OSName = 'iOS'
        }

        return OSName
    }

    const getOSVersion = () => {
        let OSVersion = 'Unknown'

        if (navigator.userAgent.indexOf('Win') != -1) {
            let winVersion = navigator.userAgent.match(/Windows NT (\d+\.\d+)/)
            if (winVersion) {
                OSVersion = winVersion[1]
            }
        } else if (navigator.userAgent.indexOf('Mac') != -1) {
            let macVersion = navigator.userAgent.match(
                /Mac OS X (\d+[._]\d+[._]\d+)/
            )
            if (macVersion) {
                OSVersion = macVersion[1].replace(/_/g, '.')
            }
        } else if (navigator.userAgent.indexOf('Linux') != -1) {
            let linuxVersion = navigator.userAgent.match(/Linux (\S+)/)
            if (linuxVersion) {
                OSVersion = linuxVersion[1]
            }
        } else if (navigator.userAgent.indexOf('Android') != -1) {
            let androidVersion = navigator.userAgent.match(/Android (\d+\.\d+)/)
            if (androidVersion) {
                OSVersion = androidVersion[1]
            }
        } else if (navigator.userAgent.indexOf('iOS') != -1) {
            let iOSVersion = navigator.userAgent.match(/OS (\d+[._]\d+[._]\d+)/)
            if (iOSVersion) {
                OSVersion = iOSVersion[1].replace(/_/g, '.')
            }
        }

        return OSVersion
    }

    const getVideoMaxResolution = () => {
        const settings = avStreamRef.current.getVideoTracks()[0].getSettings()

        return {
            width: settings.width,
            height: settings.height,
        }
    }

    return (
        <ProctoringContext.Provider
            value={{
                isVideoCaptureAvailable,
                isAudioCaptureAvailable,
                isScreenCaptureAvailable,
                startAVCapture,
                startAudioCapture,
                startVideoCapture,
                startScreenCapture,
                audioStream: audioStreamRef.current,
                videoStream: videoStreamRef.current,
                avStream: avStreamRef.current,
                screenStream: screenStreamRef.current,
                stopAVCapture,
                stopAudioCapture,
                stopVideoCapture,
                stopScreenCapture,
                attachOnCut,
                attachOnCopy,
                attachOnPaste,
                setCutEnabled,
                setCopyEnabled,
                setPasteEnabled,
                attachOnDragStart,
                attachOnDrop,
                setDragStartEnabled,
                setDropEnabled,
                setEntireScreenShareEnabled,
                attachOnWindowResize,
                attachOnWindowFocus,
                attachOnWindowBlur,
                attachOnVisibilityChange,
                setAvImageCaptureInterval,
                setScreenImageCaptureInterval,
                attachOnAvImage,
                attachOnScreenImage,
                startAvCaptureTimer,
                stopAvCaptureTimer,
                startScreenCaptureTimer,
                stopScreenCaptureTimer,
                getAvImageData,
                getScreenImageData,
                getBrowserName,
                getUserAgentData,
                getBrowserVersion,
                getOS,
                getOSVersion,
                getVideoMaxResolution,
                attachOnAvError,
                attachOnAudioError,
                attachOnVideoError,
                attachOnScreenError,
            }}
        >
            {children}
            <div
                id={PROCTORING_WRAPPER_ID}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    height: 1,
                    width: 1,
                    zIndex: -9999,
                    opacity: 0,
                    pointerEvents: 'none',
                }}
            ></div>
        </ProctoringContext.Provider>
    )
}

export default ProctoringContext
export { ProctoringProvider }
